import * as React from "react";
import {
  ChoiceGroup,
  IChoiceGroupOption,
  IChoiceGroupOptionProps,
  IChoiceGroupOptionStyles,
  IChoiceGroupStyles,
  TooltipHost,
} from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import * as Messages from "../../codegen/Messages";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormGroupValidationModeContext,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

const AzureBlueIcon = "#0058ad";

export enum OptionOrientation {
  VERTICAL = "VERTICAL",
  HORIZONTAL = "HORIZONTAL",
}

export interface IconGroupOption {
  /**
   * Disables the given option from being selected
   * @default false
   */
  disabled?: boolean;
  /**
   * The name of the icon to be displayed
   * Icon must be registered
   */
  iconName: string;
  /**
   * Unique identifier for the radio group option
   */
  id: string;
  /**
   * Whether or not the option is selected
   */
  selected?: boolean;
  /**
   * The label for this option
   * The label will not be be visually displayed to the user
   * This is left for accessibility
   */
  text: string;
  /**
   * Text to be rendered in a tooltip for the radio group option
   */
  tooltip?: string;
  /**
   * data-test-id HTML attribute value
   */
  testId?: string;
}

export interface IconGroupProps extends BaseInputProps {
  /**
   * Label for the IconGroup field
   */
  label?: string;
  /**
   * Options to be listed for selection
   */
  options: IconGroupOption[];
  /**
   * The id of the option to be selected intially
   */
  defaultValue?: string;
  /**
   * Callback when an option is selected or existing selection is changed
   */
  onChange?: (id: string) => void;
  /**
   * The layout of the options (vertically or horizontally)
   */
  optionOrientation: OptionOrientation;
}

const optionStyles: IChoiceGroupOptionStyles = {
  root: {
    backgroundColor: "none",
    margin: "0 13px 0 0",
  },
  field: {
    width: 33,
    height: 33,
    padding: 3,
    boxSizing: "border-box",
    borderColor: "transparent",
    borderRadius: "20%",
    "&:hover": { borderColor: "transparent" },
    // Hides the border of the radio button
    "&:before": { display: "none" },
    // Hides the filled in portion of the radio button when selected
    "&:after": { display: "none" },
  },
  innerField: { padding: 0 },
  // Hides the label text
  labelWrapper: { width: 0, height: 0, margin: 0, padding: 0 },
};

const selectedOptionStyles: IChoiceGroupOptionStyles = {
  ...optionStyles,
  field: {
    ...(optionStyles.field as Record<string, unknown>),
    backgroundColor: "#d7ecf9",
    borderColor: `${AzureBlueIcon} !important`,
    "&:hover": { borderColor: `${AzureBlueIcon} !important` },
  },
};

const horizontalGroupStyles: IChoiceGroupStyles = mergeStyleSets(
  { width: "100%" },
  { flexContainer: { flexDirection: "row", flexWrap: "nowrap" } },
);

const verticalGroupStyles: IChoiceGroupStyles = mergeStyleSets(
  { root: { width: "min-content" } },
  { flexContainer: { flexDirection: "column" } },
);

const iconGroupStyles: { [key in OptionOrientation]: IChoiceGroupStyles } = {
  [OptionOrientation.HORIZONTAL]: horizontalGroupStyles,
  [OptionOrientation.VERTICAL]: verticalGroupStyles,
};

const iconStyle: React.CSSProperties = { fontSize: 26 };
const selectedIconStyle: React.CSSProperties = { ...iconStyle, color: AzureBlueIcon };

/**
 * A closure which allows the onRenderField function that is returned
 * now has access to the tooltip's option
 * @returns the on render function
 */
const onRenderFieldWithTooltip = (tooltip: string) => (
  props?: IChoiceGroupOptionProps,
  render?: (props?: IChoiceGroupOptionProps) => JSX.Element | null,
): JSX.Element | null => (
  <TooltipHost content={tooltip}>
    {render?.(props)}
  </TooltipHost>
);

export type IconGroupTestIds = ComponentTestIds & LabelWrapperTestIds;

export const buildIconGroupTestIds = (testId?: string): IconGroupTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  component: buildTestId(testId),
});

/**
 * We do not expose a validator because the user may only select the given options
 */
export const IconGroup = ({
  testId,
  defaultValue,
  disabled,
  fieldName,
  groupName,
  label,
  onChange,
  optionOrientation,
  options,
  required,
  statusInfo,
  subField,
  tooltip,
}: IconGroupProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("IconGroup should be used within form");
  }
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const formErrors = React.useContext(FormErrorsContext);
  const fieldErrors = getFieldErrors(formErrors, fieldName, group);

  const [errorMessages, setErrorMessages] = React.useState<Array<string>>([]);

  const allErrors = [...errorMessages || [], ...fieldErrors || []];

  const [selectedId, setSelectedId] = React.useState(defaultValue);

  const iconGroupTestIds = buildIconGroupTestIds(testId);

  const fieldValidation = (): void => {
    const selectedValue: IconGroupOption | undefined = form.getValue(fieldName, group);
    // Check that the value set in the form
    // Is one of the values provided in the options
    const value = options.find(option => option.id === selectedValue?.id && !option.disabled);

    // If a selection is required but a value has not been set
    const requiredMessage = (required && !selectedValue) && [Messages.validation.requiredValidation()];
    // If the selected value exists and does not match a given option
    const invalidMessage = (selectedValue && !value) && [Messages.validation.radioValidation()];

    const errors = [...requiredMessage || [], ...invalidMessage || []];

    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );

    setErrorMessages(errors);
  };

  const iconGroupOptions = options.map(option => ({
    key: option.id,
    text: option.text,
    iconProps: { iconName: option.iconName },
    disabled: disabled || option.disabled,
    onRenderField: option.tooltip
      ? onRenderFieldWithTooltip(option.tooltip)
      : undefined,
    "data-test-id": option.testId,
  } as IChoiceGroupOption));

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (required || defaultValue) {
      form.registerField(fieldName, group);
    }

    if (defaultValue) {
      form.setValue({ id: defaultValue }, fieldName, group);
      fieldValidation();
    }

    return () => {
      if (required || defaultValue) {
        form.unRegisterField(fieldName, group);
      }
    };
  // Only at mount time. Other props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (required) {
        fieldValidation();
      }
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  const internalOnChange = (
    _?: React.FormEvent<HTMLElement | HTMLInputElement>,
    option?: IChoiceGroupOption,
  ): void => {
    const newSelection = {
      id: option?.key as string,
      text: option?.text as string,
    };

    setSelectedId(newSelection.id);
    form.setValue(newSelection, fieldName, group);

    if (required) {
      fieldValidation();
    }

    onChange?.(newSelection.id || "");
  };

  // Handles changing how the selected option is displayed visually
  const styledOptions = iconGroupOptions.map(option => {
    if (option.key !== selectedId) {
      option.styles = optionStyles;
      option.iconProps = { ...option.iconProps, style: iconStyle };
    } else {
      option.styles = selectedOptionStyles;
      option.iconProps = { ...option.iconProps, style: selectedIconStyle };
    }

    return option;
  });

  return (
    <LabelWrapper
      errors={allErrors}
      fieldName={fieldName}
      layout={layout}
      statusInfo={statusInfo}
      label={label}
      subField={subField}
      required={required}
      tooltip={tooltip}
      testId={testId}
    >
      <ChoiceGroup
        id={fieldName}
        data-test-id={iconGroupTestIds.component}
        options={styledOptions}
        defaultSelectedKey={defaultValue}
        onChange={internalOnChange}
        styles={iconGroupStyles[optionOrientation]}
      />
    </LabelWrapper>
  );
};
